import { bboxify } from '@mapbox/geojson-extent';
import * as turf from '@turf/turf';
import { bboxPolygon, lineString, bbox as turfBbox } from '@turf/turf';
import { Feature, GeoJsonProperties, Polygon } from 'geojson';
import {
  AnyLayer,
  ExpressionSpecification,
  FlyToOptions,
  IControl,
  Layer,
  LngLatBoundsLike,
  LngLatLike,
  MapMouseEvent,
  MapTouchEvent,
} from 'mapbox-gl';
import { getMapboxGeocode, getMapboxStyle } from '../api/mapbox';
import type { VesselList } from '../common-components/manage-vessel-lists/vessel-lists.model';
import { AlertCircleStyle } from '../maritime-menu-options/alert-panel/alerting-vessels-labels';
import type {
  FleetsVessel,
  FleetVisibility,
  NormalisedFleetsVessels,
} from '../maritime-menu-options/fleets-panel/fleets.model';
import type {
  FleetColouredBySelectedColour,
  FleetColouredByVesselType,
} from '../maritime-menu-options/fleets-panel/manage-fleets/manage-fleets.slice';
import { isFleetVessel } from '../maritime-menu-options/my-fleet-panel/myFleetVessels.model';
import { setHistoricVesselsColouring } from '../maritime-menu-options/tools-panel/historic-area-search/historic-area-search.slice';
import { setVesselTypeDefaultColouring as setNearbyDefaultColour } from '../maritime-menu-options/tools-panel/nearby-vessels/nearby-vessels.slice';
import { VesselSuperTypeColours } from '../models/vessels/all-ship-types';
import { type Vessel, VesselSource } from '../models/vessels/vessel.model';
import geollectTheme from '../mui-themes/geollect';
import { setAlertDefaultColouring } from '../state/alerts/alerts.slice';
import { setVesselTypeDefaultColouring as setFleetsDefaultColour } from '../state/fleet-vessels/fleet-vessels.slice';
import { setTickerOpen } from '../state/incidents/incidents.slice';
import store from '../store';
import { getDefaultMapboxStyle } from '../theme';
import { nearlyEquals } from '../utils/measurement-helpers';
import { UserPreferencesBaseMap } from '../utils/user.enum';
import {
  getFallbackColor,
  isValidHexColor,
} from '../utils/vessel-colour.utils';
import {
  VESSEL_COLOUR_GREEN,
  VESSEL_COLOUR_NAVY,
  VESSEL_COLOUR_NEARBY,
} from '../utils/vessel-constants.utils';
import { prepareVesselColourData } from '../utils/vessel-styling.utils';
import CustomMapEvents from './custom-map-events.enum';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  type MapExtent,
  setCentre,
  setFitBounds,
  setFlyTo,
  setMapExtent,
  setMapStyle,
  setMapZoom,
  setPaintProperty,
  setStyle,
} from './map.slice';
import { getMapInstance } from './map-instance';
import MapLayer from './map-layer-manager/map-layer.enum';
import MapLayerVisibility from './map-layer-manager/map-layer-visibility.enum';
import MapStyle from './map-style';

export interface ExtendedMapEvent extends MapMouseEvent {
  isDrawing?: boolean;
  layerClickHandled?: boolean;
}

const userPreferencesBaseMapStyle = {
  [UserPreferencesBaseMap.DEFAULT]: MapStyle.DEFAULT,
  [UserPreferencesBaseMap.SATELLITE]: MapStyle.SATELLITE_IMAGERY,
};

const buildMapboxCaseStatement = (vesselIds: string[], opacity: number) => {
  // get the vessel_id or unique_vessel_identifier, whichever is not null
  const coalesce = [
    'coalesce',
    ['get', 'vessel_id'],
    ['get', 'unique_vessel_identifier'],
  ];
  // ['case', boolean, ifTrue, ifFalse]
  return [
    'case',
    // if vessel_id or unique_vessel_identifier is in vesselIds array
    ['in', coalesce, ['literal', vesselIds]],
    opacity,
    1,
  ];
};

namespace MapHelpers {
  const { mapZoom } = store.getState().map;

  export const getLayer = (sourceId: string): AnyLayer | null => {
    const map = getMapInstance();
    try {
      if (map && map.getLayer(sourceId)) {
        return map.getLayer(sourceId);
      }
    } catch {
      // sometimes there will be a map but getLayer doesn't exist
    }
    return null;
  };

  export const setFillLayerOpacity = async (
    layerId: MapLayer,
    opacity: number
  ) => {
    const layer = getLayer(layerId);
    if (layer) {
      store.dispatch(
        setPaintProperty({
          layerId,
          name: 'fill-opacity',
          value: opacity / 100,
        })
      );
    }
  };

  export const setVesselOpacity = (
    vesselIds: string[],
    // if layerId is a function, it will be called with the vesselId as an argument
    layerId: MapLayer | string | ((vesselId: string) => string),
    opacity: number
  ) => {
    if (layerId instanceof Function) {
      vesselIds.forEach((vesselId) => {
        const layer = getLayer(layerId(vesselId));
        if (layer) {
          store.dispatch(
            setPaintProperty({
              layerId: layerId(vesselId),
              name: 'icon-opacity',
              value: buildMapboxCaseStatement([vesselId], opacity),
            })
          );
        }
      });
    } else {
      if (!getLayer(layerId)) {
        return;
      }
      store.dispatch(
        setPaintProperty({
          layerId,
          name: 'icon-opacity',
          value: buildMapboxCaseStatement(vesselIds, opacity),
        })
      );
    }
  };

  export const setLayoutProperty = (
    layer: string,
    name: string,
    value: any
  ) => {
    const map = getMapInstance();
    if (map && map.getLayer(layer)) {
      map.setLayoutProperty(layer, name, value);
    }
  };

  export const getLayoutProperty = (layer: string, name: string) => {
    const map = getMapInstance();
    return map ? map.getLayoutProperty(layer, name) : undefined;
  };

  export const setLayerOpacity = (layer: string, opacity: number) => {
    store.dispatch(
      setPaintProperty({
        layerId: layer,
        name: 'raster-opacity',
        value: opacity / 100,
      })
    );
  };

  export const getLayerOpacity = (layer: string) => {
    const map = getMapInstance();
    return map ? map.getPaintProperty(layer, 'raster-opacity') : undefined;
  };

  export const isRasterLayer = (layerId: string) => {
    const layer = getLayer(layerId);
    return layer && layer.type === 'raster';
  };

  export const setVesselIcon = (
    vessel: Vessel,
    layerId: MapLayer,
    icon: string
  ) => {
    // https://docs.mapbox.com/mapbox-gl-js/style-spec/expressions/#match

    if (isFleetVessel(vessel)) {
      MapHelpers.setLayoutProperty(layerId, 'icon-image', [
        'match',
        ['get', 'vessel_id'],
        vessel.vessel_id,
        icon,
        // fallback icon. use default one here
        MapHelpers.getLayoutProperty(layerId, 'icon-image'),
      ]);
    } else {
      // Use mmsi if no vessel id available
      MapHelpers.setLayoutProperty(layerId, 'icon-image', [
        'match',
        ['get', 'mmsi'],
        vessel.mmsi,
        icon,
        // fallback icon. use default one here
        MapHelpers.getLayoutProperty(layerId, 'icon-image'),
      ]);
    }
  };

  export const isLayerVisible = (layerSource: string) => {
    const layer = getLayer(layerSource)!;
    return (
      layer &&
      getLayoutProperty(layerSource, 'visibility') ===
        MapLayerVisibility.VISIBLE
    );
  };

  export const LayerVisibilityChangeType = (layerId: string) =>
    `${layerId}_${CustomMapEvents.LAYER_VISIBILITY_CHANGE}`;

  export const fire = (type: string, properties?: {}) => {
    const map = getMapInstance();
    if (map) {
      map.fire(type, properties);
    }
  };

  export const getSource = (sourceId: string) => {
    const map = getMapInstance();
    if (map && map.getSource(sourceId)) {
      return map.getSource(sourceId);
    }
    return null;
  };

  export const triggerLayerVisibilityChange = (layerId: string) => {
    fire(LayerVisibilityChangeType(layerId), getSource(layerId)!);
  };

  export const removeMapEventListener = (
    type: string,
    callback:
      | ((e: mapboxgl.MapMouseEvent) => void)
      | ((e: mapboxgl.MapTouchEvent) => void)
  ) => {
    const map = getMapInstance();
    try {
      if (map) {
        map.off(type, callback);
      }
    } catch {
      // no map event listener to remove
    }
  };

  export const removeLayer = (layerId: string) => {
    const map = getMapInstance();
    if (map) {
      map.removeLayer(layerId);
    }
  };

  export const removeLayerVisibilityEvent = (
    layerId: string,
    callback: (ev: any) => void
  ) => {
    removeMapEventListener(LayerVisibilityChangeType(layerId), callback);
  };

  export const setLayerVisibility = async (
    layerId: string,
    visible: boolean
  ) => {
    switch (layerId) {
      case MapLayer.INCIDENTS:
        store.dispatch(setTickerOpen(visible));
        break;
      default:
        break;
    }
    setLayoutProperty(
      layerId,
      'visibility',
      visible ? MapLayerVisibility.VISIBLE : MapLayerVisibility.NOT_VISIBLE
    );
    triggerLayerVisibilityChange(layerId);
  };

  export const setLayerVisibilityIfExists = async (
    layerSource: string,
    visible: boolean
  ) => {
    if (getLayer(layerSource)) {
      setLayerVisibility(layerSource, visible);
    }
  };

  // currently used in Alerts but will either set the vessels to be coloured
  // the same as the alert colour or set them to the standard alert vessel
  // colour
  export const swapVesselColouring = (layerSource: string) => {
    const colouredLayer = `${layerSource}-coloured`;
    if (isLayerVisible(layerSource)) {
      setLayerVisibilityIfExists(layerSource, false);
      setLayerVisibilityIfExists(colouredLayer, true);
    } else {
      setLayerVisibilityIfExists(layerSource, true);
      setLayerVisibilityIfExists(colouredLayer, false);
    }
  };

  export const setVesselMultiColour = (layerId: MapLayer) => {
    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: [
          'match',
          ['get', 'alertType'],
          `AIS_OFF`,
          `${AlertCircleStyle.AIS_OFF.backgroundColor}`,
          `AIS_ON`,
          `${AlertCircleStyle.AIS_ON.backgroundColor}`,
          `ENTER_ROI`,
          `${AlertCircleStyle.ENTER_ROI.backgroundColor}`,
          `EXIT_ROI`,
          `${AlertCircleStyle.EXIT_ROI.backgroundColor}`,
          `STATIC_CHANGE`,
          `${AlertCircleStyle.STATIC_CHANGE.backgroundColor}`,
          `STS`,
          `${AlertCircleStyle.STS.backgroundColor}`,
          `INCIDENTS`,
          `${AlertCircleStyle.INCIDENTS.backgroundColor}`,
          `INCIDENTS_ROUTE`,
          `${AlertCircleStyle.INCIDENTS_ROUTE.backgroundColor}`,
          // else
          VESSEL_COLOUR_NAVY,
        ],
      })
    );

    store.dispatch(setAlertDefaultColouring(true));
  };

  export const vesselSuperTypeColorExpression: ExpressionSpecification = [
    'match',
    ['get', 'vessel_super_type'],
    'Cargo',
    VesselSuperTypeColours.Cargo,
    'Fishing',
    VesselSuperTypeColours.Fishing,
    'High Speed Craft',
    VesselSuperTypeColours['High Speed Craft'],
    'Passenger',
    VesselSuperTypeColours.Passenger,
    'Pleasure Craft',
    VesselSuperTypeColours['Pleasure Craft'],
    'Tanker',
    VesselSuperTypeColours.Tanker,
    'Tugs And Special Craft',
    VesselSuperTypeColours['Tugs And Special Craft'],
    'Other',
    VesselSuperTypeColours.Other,
    // else goto default
    VesselSuperTypeColours.Other,
  ];

  export const setFleetVesselTypeMultiColour = (
    layerId: MapLayer,
    fleetVesselIds: FleetColouredByVesselType[],
    defaultVesselColour: string
  ) => {
    const fleetExpressions = fleetVesselIds.map(
      ({ vessel_list_id, vessel_ids }) => [
        'all',
        ['in', vessel_list_id, ['get', 'vessel_list_id']],
        ['in', ['get', 'vessel_id'], ['literal', vessel_ids]],
      ]
    );

    const conditionalExpression: ExpressionSpecification = [
      'case',
      ...fleetExpressions.flatMap((expression) => [
        expression,
        vesselSuperTypeColorExpression,
      ]),
      // Default color for non-matching vessel IDs
      defaultVesselColour,
    ];

    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: conditionalExpression,
      })
    );
  };

  export const setFleetIconsToColourOptions = (
    layerId: MapLayer,
    fleetVesselIdsByType: FleetColouredByVesselType[],
    fleetVesselIdsBySelectedColour: FleetColouredBySelectedColour[],
    defaultVesselColour: string
  ) => {
    const vesselTypeExpressions = fleetVesselIdsByType.map(
      ({ vessel_list_id, vessel_ids }) => [
        'all',
        ['in', vessel_list_id, ['get', 'vessel_list_id']],
        ['in', ['get', 'vessel_id'], ['literal', vessel_ids]],
        vesselSuperTypeColorExpression,
      ]
    );

    const selectedColourExpressions = fleetVesselIdsBySelectedColour.map(
      ({ vessel_list_id, vessel_ids, colour }) => [
        'all',
        ['in', vessel_list_id, ['get', 'vessel_list_id']],
        ['in', ['get', 'vessel_id'], ['literal', vessel_ids]],
        colour,
      ]
    );

    const conditionalExpression: ExpressionSpecification = [
      'case',
      ...vesselTypeExpressions.flatMap((expression) => [
        expression.slice(0, -1),
        expression[expression.length - 1],
      ]),
      ...selectedColourExpressions.flatMap((expression) => [
        expression.slice(0, -1),
        expression[expression.length - 1],
      ]),
      // Default color for non-matching vessel IDs
      defaultVesselColour,
    ];

    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: conditionalExpression,
      })
    );
  };

  export const setFleetIconsToColour = (
    layerId: MapLayer,
    fleetVesselIds: FleetColouredBySelectedColour[],
    defaultVesselColour: string
  ) => {
    const fleetExpressions = fleetVesselIds.map(
      ({ vessel_list_id, vessel_ids, colour }) => [
        'all',
        ['in', vessel_list_id, ['get', 'vessel_list_id']],
        ['in', ['get', 'vessel_id'], ['literal', vessel_ids]],
        colour,
      ]
    );

    const conditionalExpression: ExpressionSpecification = [
      'case',
      ...fleetExpressions.flatMap((expression) => [
        expression.slice(0, -1),
        expression[expression.length - 1],
      ]),
      // Default color for non-matching vessel IDs
      defaultVesselColour,
    ];

    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: conditionalExpression,
      })
    );
  };

  export const setVesselIconsToListColours = (
    layerId: MapLayer,
    lists: VesselList[],
    filteredVesselLists: string[]
  ) => {
    if (!lists || lists.length === 0) return;

    let listExpressions: [ExpressionSpecification, ExpressionSpecification][] =
      [];

    // If filtered lists are provided, prioritize them
    if (filteredVesselLists && filteredVesselLists.length > 0) {
      listExpressions = filteredVesselLists.reduce<
        [ExpressionSpecification, ExpressionSpecification][]
      >((acc, filteredListId) => {
        const matchingList = lists.find(
          (list) => list.list_id === filteredListId
        );
        if (matchingList) {
          acc.push([
            [
              'in',
              ['get', 'vessel_id'],
              ['literal', matchingList.vessels.map((v) => v.vessel_id)],
            ],
            ['literal', matchingList.list_colour || getFallbackColor()],
          ]);
        }
        return acc;
      }, []);
    }

    // If no filtered lists or fallback to first list with a color
    if (listExpressions.length === 0) {
      listExpressions = lists.reduce<
        [ExpressionSpecification, ExpressionSpecification][]
      >((acc, list) => {
        const listColour = isValidHexColor(list.list_colour)
          ? list.list_colour
          : null;

        if (listColour) {
          acc.push([
            [
              'in',
              ['get', 'vessel_id'],
              ['literal', list.vessels.map((v) => v.vessel_id)],
            ],
            ['literal', listColour],
          ]);
        }
        return acc;
      }, []);

      // If no valid list colors were found, use the fallback color
      if (listExpressions.length === 0) {
        listExpressions.push([
          ['literal', true], // Default condition
          ['literal', getFallbackColor()], // Fallback color
        ]);
      }
    }

    const conditionalExpression: ExpressionSpecification = [
      'case',
      ...listExpressions.flatMap(([condition, result]) => [condition, result]),
      // If not in a list, fallback to user preference or source-based color
      [
        'match',
        ['get', 'source'],
        VesselSource.AIS,
        getFallbackColor(),
        VesselSource.RISK_INTELLIGENCE,
        VESSEL_COLOUR_GREEN,
        getFallbackColor(),
      ],
    ];

    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: conditionalExpression,
      })
    );
  };

  export const setVesselTypeMultiColour = (layerId: MapLayer) => {
    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: vesselSuperTypeColorExpression,
      })
    );
    if (layerId === MapLayer.MY_FLEET_VESSELS) {
      store.dispatch(setFleetsDefaultColour(true));
    }
    if (layerId === MapLayer.NEARBY_VESSELS) {
      store.dispatch(setNearbyDefaultColour(true));
    }
    if (layerId === MapLayer.HISTORIC_VESSELS) {
      store.dispatch(setHistoricVesselsColouring(true));
    }
  };

  export const setVesselTypeSingleColour = (layerId: MapLayer) => {
    let colourExpression = null;

    if (layerId === MapLayer.MY_FLEET_VESSELS) {
      store.dispatch(setFleetsDefaultColour(false));
    }

    if (layerId === MapLayer.NEARBY_VESSELS) {
      colourExpression = [
        'literal',
        VESSEL_COLOUR_NEARBY,
      ] as mapboxgl.ExpressionSpecification;
      store.dispatch(setNearbyDefaultColour(false));
    }

    if (layerId === MapLayer.HISTORIC_VESSELS) {
      const { userPreferences } = store.getState().userPreferences;
      colourExpression = prepareVesselColourData(userPreferences);
      store.dispatch(setHistoricVesselsColouring(false));
    }

    if (colourExpression === null) {
      return;
    }

    store.dispatch(
      setPaintProperty({
        layerId,
        name: 'icon-color',
        value: colourExpression,
      })
    );
  };

  export const createZoomPadding = (padding = 160) => {
    let left = padding;
    let bottom = padding;

    // Zoom to Feature and put in center of map viewport
    // TODO: Take in to account other menus / elements in front of map element.
    const app = document.querySelector('.App');
    const mainMenuEl = app?.querySelector('.menu-container')?.children[0];

    if (mainMenuEl) {
      const boundingClientRect = mainMenuEl.getBoundingClientRect();

      left += boundingClientRect.left + boundingClientRect.width;
      if (left > window.innerWidth / 2.5) {
        // if the left padding is more than 40% of the screen width, set it to 0
        // otherwise mapbox  may throw an error. (40% was an experimentally determined value and may need to be adjusted)
        return {
          padding: {
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
          },
        };
      }
    }

    const secondaryPaneElement = app?.querySelector(
      '.secondary-pane-container'
    );
    if (secondaryPaneElement) {
      const boundingClientRect = secondaryPaneElement.getBoundingClientRect();
      if (boundingClientRect.top > 0) bottom += boundingClientRect.height;
    }

    return {
      padding: {
        top: padding,
        left,
        bottom,
        right: padding,
      },
    };
  };

  export const flyTo = (options: {}) => {
    store.dispatch(setFlyTo(options));
  };

  const isValidPoint = (
    coordinates: mapboxgl.LngLatLike | undefined
  ): coordinates is mapboxgl.LngLatLike =>
    coordinates !== undefined &&
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number';

  export const zoomToPoint = (
    coordinates: mapboxgl.LngLatLike,
    zoom = 7,
    preventZoomOut: boolean = false
  ) => {
    if (!isValidPoint(coordinates)) {
      return;
    }
    const map = getMapInstance();
    if (!map) {
      return;
    }
    const currentZoom = map.getZoom();
    const newZoom =
      preventZoomOut && currentZoom > zoom
        ? currentZoom
        : Math.max(mapZoom, zoom);

    flyTo({
      ...createZoomPadding(),
      zoom: newZoom,
      around: coordinates,
      center: coordinates,
      curve: 2,
      offset: [0, 0],
      speed: 0.5,
      animate: true,
    });
  };

  export const zoomToPointFast = (
    coords: mapboxgl.LngLatLike,
    options: Partial<mapboxgl.FlyToOptions> = { zoom: 10 }
  ) => {
    const defaultOptions: mapboxgl.FlyToOptions = {
      ...createZoomPadding(),
      center: coords,
      curve: 1.4,
      speed: 1.8,
      zoom: Math.max(mapZoom, options.zoom ?? 10),
      ...options,
    };
    if (options.duration) {
      delete defaultOptions.speed;
    }
    flyTo(defaultOptions);
  };

  // return to a centered globe view. Good for when the map is likely to shortly go somewhere else but we're not yet sure where.
  export const returnToGlobeView = () => {
    const map = getMapInstance();
    if (!map) {
      return;
    }
    map.flyTo({
      ...createZoomPadding(),
      center: DEFAULT_MAP_CENTER, // use the default center of the map
      zoom: DEFAULT_MAP_ZOOM, // use default zoom
      curve: 1.4,
      speed: 1.8,
      animate: true,
    });
  };

  // Useful to check if the map is currently in a globe view.
  // If the user has moved the map from this 'default' position,
  // we should not interrupt them with another jarring move
  export const isInGlobeView = () => {
    const map = getMapInstance();
    if (!map) {
      return false;
    }
    const center = map.getCenter();
    const zoom = map.getZoom();
    return (
      // floating point errors mean the numbers may not exactly match
      nearlyEquals(center.lng, DEFAULT_MAP_CENTER[0]) &&
      nearlyEquals(center.lat, DEFAULT_MAP_CENTER[1]) &&
      zoom === DEFAULT_MAP_ZOOM
    );
  };

  export const once = (
    type: string,
    listener: (ev: mapboxgl.MapMouseEvent) => void
  ) => {
    const map = getMapInstance();
    if (map) {
      map.once(type, listener);
    }
  };

  export const queryRenderedFeatures = (
    point: mapboxgl.Point | undefined,
    options?: {}
  ) => {
    const map = getMapInstance();
    if (!map || !point) {
      return [];
    }
    return map.queryRenderedFeatures(point, options);
  };

  export const addMapEventListener = (
    eventName: string,
    layer: string | string[] | null,
    callback: (e: any) => void
  ) => {
    const map = getMapInstance();
    if (!map) {
      return;
    }
    if (layer) {
      map.on(eventName as any, layer!, callback);
    } else {
      map.on(eventName as any, callback);
    }
  };

  export const zoomToBBox = (bbox: LngLatBoundsLike) => {
    /*
     * Bug in mapbox, using fitBounds after a flyTo adds their paddings together
     * which can cause there to be less screen than padding, which causes fitbounds to fail.
     * See https://github.com/mapbox/mapbox-gl-js/issues/11831
     * Workaround: setPadding back to 0 manually.
     * Padding now set in fitBounds useEffect hook in maps.tsx
     */
    store.dispatch(
      setFitBounds({
        bounds: bbox,
        options: {
          ...createZoomPadding(),
        },
      })
    );
  };

  export const zoomToFeatureCollection = (
    featureCollection: GeoJSON.GeoJSON,
    options?: mapboxgl.MapboxOptions['fitBoundsOptions']
  ) => {
    try {
      // If feature already possesses a bbox, use it
      const bounds = featureCollection.bbox || bboxify(featureCollection).bbox!;
      /*
       * Bug in mapbox, using fitBounds after a flyTo adds their paddings together
       * which can cause there to be less screen than padding, which causes fitbounds to fail.
       * See https://github.com/mapbox/mapbox-gl-js/issues/11831
       * Workaround: setPadding back to 0 manually.
       * Padding now set in fitBounds useEffect hook in maps.tsx
       */
      store.dispatch(
        setFitBounds({
          bounds: bounds as LngLatBoundsLike,
          options: {
            ...createZoomPadding(),
            ...options,
          },
        })
      );
    } catch {
      // Handle the error
    }
  };

  export const zoomToLine = (point1: LngLatLike, point2: LngLatLike) => {
    const line = lineString([
      point1 as [number, number],
      point2 as [number, number],
    ]);
    const boundingBox = turfBbox(line);
    const boundingBoxPolygon = bboxPolygon(boundingBox);

    // map.fitBounds(bounds, createZoomPadding(200));
    zoomToFeatureCollection(boundingBoxPolygon, { maxZoom: 21 });
  };

  export const zoomToFeature = (feature: GeoJSON.Feature) => {
    // Get the bounding box of the feature
    const boundingBox = turfBbox(feature);

    // Create a polygon from the bounding box
    const boundingBoxPolygon = bboxPolygon(boundingBox);

    // Zoom to the polygon
    zoomToFeatureCollection(boundingBoxPolygon, { maxZoom: 21 });
  };

  export const zoomToPolygon = (polygon: GeoJSON.Polygon) => {
    // Get the bounding box of the polygon
    const boundingBox = turfBbox(polygon);

    // Create a polygon from the bounding box
    const boundingBoxPolygon = bboxPolygon(boundingBox);

    // Zoom to the polygon
    zoomToFeatureCollection(boundingBoxPolygon, { maxZoom: 21 });
  };

  /**
   * When the user clicks the map, we first want to check if they've clicked one of our features
   * (ports, incidents, boundaries, etc.)
   * If they didn't, we want to check if they had clicked a country by querying the mapbox geocode api
   * @param map
   * @param event
   * @param token Mapbox Access Token for API calls
   */
  export const countryUnderMouse = async (
    event: MapMouseEvent | MapTouchEvent
  ) => {
    // if the clicked point is not on the surface, we want to abort,
    // as event.lngLat will be mapped to the nearest point on the surface which is barely visible
    const map = getMapInstance();
    if (!map || !map.isPointOnSurface(event.point)) {
      return undefined;
    }

    const result = await getMapboxGeocode(event.lngLat.lng, event.lngLat.lat);
    // The api has a default 'limit' of 1, so features length should be 1 or 0.
    if (result.features.length > 0) {
      const feature = result.features[0];
      return feature;
    }
    return undefined;
  };

  export const removeSource = (sourceId: string) => {
    const map = getMapInstance();
    if (map) {
      map.removeSource(sourceId);
    }
  };

  export const deleteSource = (sourceId: string) => {
    // Remove labels layer if it exists
    if (getLayer(`${sourceId}_labels`)) {
      removeLayer(`${sourceId}_labels`);
    }

    // Remove radius aura layers if they exist
    if (getLayer(`${sourceId}_radius`)) {
      removeLayer(`${sourceId}_radius`);
    }

    if (getSource(sourceId)) {
      removeLayer(sourceId);
      removeSource(sourceId);
    }

    if (getSource(`${sourceId}_labels`)) {
      removeSource(`${sourceId}_labels`);
    }

    if (getSource(`${sourceId}_radius`)) {
      removeSource(`${sourceId}_radius`);
    }
  };

  export const deleteSources = (sourceIds: string[]) => {
    sourceIds.forEach((sourceId) => {
      deleteSource(sourceId);
    });
  };

  export const addSource = (
    sourceId: string,
    options: mapboxgl.AnySourceData
  ) => {
    const map = getMapInstance();
    if (map) {
      map.addSource(sourceId, options);
    }
  };

  export const addLayer = (layer: mapboxgl.AnyLayer) => {
    const map = getMapInstance();
    if (map) {
      map.addLayer(layer);
    }
  };

  export const layerExists = (layerId: string): boolean => {
    const map = getMapInstance();
    return !!(map && map.getLayer(layerId));
  };

  export const moveLayer = (layerId: string, beforeId?: string) => {
    const map = getMapInstance();
    if (layerExists(layerId)) {
      if (beforeId && layerExists(beforeId) && map) {
        map.moveLayer(layerId, beforeId);
      }
      if (map) {
        map.moveLayer(layerId);
      }
    }
  };

  export const getStyle = () => {
    const map = getMapInstance();
    return map ? map.getStyle() : undefined;
  };

  export const getStyleValid = () => {
    const map = getMapInstance();
    try {
      return map?.getStyle?.();
    } catch (e) {
      if ((e as Error).message.includes("(reading 'version')")) {
        // mapbox bug, ignore
        return false;
      }
      throw e;
    }
  };

  export const setVesselSingleColour = (
    layerId: MapLayer,
    value: string | ExpressionSpecification = '#FF0000'
  ) => {
    const map = getMapInstance();
    if (getStyleValid() && map) {
      map.setPaintProperty(layerId, 'icon-color', value);
      store.dispatch(setAlertDefaultColouring(false));
    }
  };

  export const getAllLayers = () => {
    let layers: AnyLayer[] = [];
    // Try Catch added because sometimes map.getStyle() temporarily throws error on
    // resizing the map

    try {
      const style = getStyle();
      if (style && style.layers) {
        layers = style.layers;
        return layers.map((layer) => getLayer(layer.id)!);
      }
      return layers;
    } catch {
      return layers;
    }
  };

  // https://docs.mapbox.com/mapbox-gl-js/api/map/#map#movelayer
  export const moveLayerIfExists = (
    layerId: string,
    beforeLayerId?: string
  ) => {
    if (!beforeLayerId) {
      if (getLayer(layerId)) {
        moveLayer(layerId);
      }
      return;
    }
    if (getLayer(layerId) && getLayer(beforeLayerId)) {
      moveLayer(layerId, beforeLayerId);
    }
  };

  export const checkMapExtentValidity = (extent: MapExtent) => {
    if (
      !extent ||
      !extent.center ||
      !extent.zoom ||
      extent.center[0] < -180 ||
      extent.center[0] > 180 ||
      extent.center[1] < -90 ||
      extent.center[1] > 90 ||
      extent.zoom < 0 ||
      extent.zoom > 22
    ) {
      return false;
    }
    return true;
  };

  export const updateMapExtent = (extent: MapExtent) => {
    if (checkMapExtentValidity(extent)) {
      store.dispatch(setMapExtent(extent));
      store.dispatch(setCentre(extent.center));
      store.dispatch(setMapZoom(extent.zoom));
    }
  };

  export const updateMapStyle = async (basemap: UserPreferencesBaseMap) => {
    const map = getMapInstance();
    let currentStyle: mapboxgl.Style | undefined;
    try {
      currentStyle = map?.getStyle?.();
    } catch (e) {
      if ((e as Error).message.includes("(reading 'version')")) {
        // mapbox bug, ignore
        return;
      }
      throw e;
    }

    const mapStyle =
      basemap === UserPreferencesBaseMap.DEFAULT
        ? getDefaultMapboxStyle()
        : userPreferencesBaseMapStyle[basemap];

    const loadFallbackStyle = () => {
      store.dispatch(
        setMapStyle({ style: mapStyle, options: { diff: false } })
      );
    };

    store.dispatch(setStyle(mapStyle));

    try {
      if (!currentStyle) {
        loadFallbackStyle();
        return;
      }

      // https://github.com/mapbox/mapbox-gl-js/issues/4006#issuecomment-772462907
      // Merge current layers with layers from the new style

      const mapStyleResponse = await getMapboxStyle(
        mapStyle.replaceAll('mapbox://styles/', '')
      );
      const newStyle = mapStyleResponse;
      // ensure any sources from the current style are copied across to the new style
      newStyle.sources = { ...currentStyle.sources, ...newStyle.sources };

      let labelIndex = newStyle.layers.findIndex(
        (el: Layer) => el.id === 'waterway-label'
      );

      // default to on top
      if (labelIndex === -1) {
        labelIndex = newStyle.layers.length;
      }
      const appLayers = (currentStyle.layers as Layer[]).filter(
        (el) =>
          // app layers are the layers to retain, and these are any layers which have a different source set
          el.source &&
          el.source !== 'mapbox://mapbox.satellite' &&
          el.source !== 'mapbox' &&
          el.source !== 'composite'
      );

      newStyle.layers = [
        ...newStyle.layers.slice(0, labelIndex),
        ...appLayers,
        ...newStyle.layers.slice(labelIndex, -1),
      ];
      store.dispatch(setMapStyle({ style: newStyle }));
    } catch (_error) {
      loadFallbackStyle();
    }
  };

  export const onLayerVisibilityChange = (
    layerId: string,
    callback: (ev: any) => void
  ) => {
    addMapEventListener(LayerVisibilityChangeType(layerId), null, callback);
  };

  export const addImage = (
    imageName: string,
    img: HTMLImageElement,
    options?: {}
  ) => {
    const map = getMapInstance();
    if (map) {
      if (map.hasImage && map.hasImage(imageName)) {
        map.removeImage(imageName);
      }
      map.addImage(imageName, img, options);
    }
  };

  export const loadImage = (
    imageName: string,
    callback: (
      error: Error | null | undefined,
      result?: HTMLImageElement | ImageBitmap | ImageData | null
    ) => void
  ) => {
    const map = getMapInstance();
    if (map) {
      return map.loadImage(imageName, callback);
    }
    return undefined;
  };

  export const easeTo = (options: FlyToOptions) => {
    const map = getMapInstance();
    if (map) {
      map.easeTo(options);
    }
  };

  export const querySourceFeatures = (source: string) => {
    const map = getMapInstance();
    return map ? map.querySourceFeatures(source) : [];
  };

  export const setFilter = (layer: string, options?: any[]) => {
    const map = getMapInstance();
    if (map) {
      map.setFilter(layer, options);
    }
  };

  export const setNonVesselObjectsVisibility = (
    layer: string,
    renderNonVesselObjects: boolean
  ) => {
    const map = getMapInstance();
    if (!map) {
      return;
    }
    if (!renderNonVesselObjects) {
      map.setFilter(layer, [
        'all',
        ['!', ['in', 'BUOY', ['get', 'name']]],
        ['!', ['in', 'BOUEE', ['get', 'name']]],
        ['!', ['in', 'NET', ['get', 'name']]],
      ]);
    } else {
      map.setFilter(layer, undefined);
    }
  };

  /**
   * Sets a filter on the specified map layer to hide the given vessel IDs.
   * If the vesselIds array is empty, the filter is removed from the layer.
   *
   * @param {string} layer - The name of the map layer to set the filter on.
   * @param {string[]} vesselIds - An array of vessel IDs to filter the layer by.
   */
  export const setFilterVesselIds = (layer: string, vesselIds: string[]) => {
    const map = getMapInstance();
    if (!map) {
      return;
    }
    if (vesselIds.length === 0) {
      map.setFilter(layer, undefined);
    } else {
      map.setFilter(layer, [
        '!',
        ['in', ['get', 'vesselId'], ['literal', vesselIds]],
      ]);
    }
  };

  export const addControl = (
    control: IControl,
    position?:
      | 'top-right'
      | 'top-left'
      | 'bottom-right'
      | 'bottom-left'
      | undefined
  ) => {
    const map = getMapInstance();
    if (map) {
      map.addControl(control, position);
    }
  };

  export const getCanvas = () => {
    const map = getMapInstance();
    return map ? map.getCanvas() : undefined;
  };

  export const getCenter = () => {
    const map = getMapInstance();
    return map ? map.getCenter() : undefined;
  };

  export const resize = () => {
    const map = getMapInstance();
    if (map) {
      map.resize();
    }
  };

  export const loaded = () => {
    const map = getMapInstance();
    if (map && Object.keys(map).length > 0) {
      return map.loaded();
    }
    return null;
  };

  /**
   * Buffer a point in 'screen-space' ie, screen pixels, rather than lat long.
   * queryRenderedFeatures can be given a [x,y] [x,y] pair in screen space, so this is useful for getting features near to a clicked point.
   * @param point
   * @param options either pass the percentage of screen width or number of pixels in each direction. Recommend screen width.
   * @returns a pair of points, the first is the top left of the buffer, the second is the bottom right.
   */
  export const bufferPoint = (
    point: mapboxgl.Point,
    options: { percent: number } | { pixels: number }
  ): [mapboxgl.PointLike, mapboxgl.PointLike] => {
    const map = getMapInstance();
    if ('percent' in options) {
      if (!map) {
        return [
          [point.x, point.y],
          [point.x, point.y],
        ];
      }
      const { width } = map.getCanvas();
      // if we want 5% of the width, we want 2.5% of the width on each side of the point
      // also need to take into account the device pixel ratio
      // only use width so the buffer is square rather than rectangular
      const adjustedWidth =
        (width / window.devicePixelRatio / 2) * (options.percent / 100);
      return [
        [point.x - adjustedWidth, point.y - adjustedWidth],
        [point.x + adjustedWidth, point.y + adjustedWidth],
      ];
    }
    return [
      [point.x - options.pixels, point.y - options.pixels],
      [point.x + options.pixels, point.y + options.pixels],
    ];
  };

  export const removeSearchAreaDrawLayers = (layers: any, id: string) => {
    if (layers.length > 0) {
      layers.forEach((layer: any) => {
        if (layer.id?.includes(`${id}-fill`)) {
          MapHelpers.removeLayer(layer.id);
        }
        if (layer.id?.includes(`${id}-outline`)) {
          MapHelpers.removeLayer(layer.id);
        }
      });
      if (MapHelpers.getSource(`${id}`)) MapHelpers.removeSource(`${id}`);
    }
  };

  export const addBoxAreaOutlineLayer = (
    coordinates: any,
    layers: any,
    id: string,
    colour: string = geollectTheme.palette.divider
  ) => {
    removeSearchAreaDrawLayers(layers, id);

    MapHelpers.addSource(id, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates,
        },
      },
    });

    MapHelpers.addLayer({
      id: `${id}-fill`,
      type: 'fill',
      source: id,
      layout: {},
      paint: {
        'fill-color': colour,
        'fill-opacity': 0.1,
      },
    });

    MapHelpers.addLayer({
      id: `${id}-outline`,
      type: 'line',
      source: id,
      layout: {},
      paint: {
        'line-color': colour,
        'line-width': 2,
      },
    });
  };

  export const createPolygonFromBox = (
    boxLngLat: mapboxgl.LngLat[]
  ): Feature<Polygon, GeoJsonProperties> => {
    const [p1, p2] = boxLngLat;
    return turf.bboxPolygon([p1.lng, p1.lat, p2.lng, p2.lat]);
  };

  export const addPolygonToMap = (
    polygon: Feature<Polygon, GeoJsonProperties>,
    layers: any,
    id: string,
    colour?: string
  ) => {
    const coordinates = [...polygon.geometry.coordinates];
    MapHelpers.addBoxAreaOutlineLayer(coordinates, layers, id, colour);
    MapHelpers.zoomToFeature(polygon);
    return polygon;
  };

  export const addBoxToMap = (
    boxLngLat: mapboxgl.LngLat[],
    layers: any,
    id: string,
    colour?: string
  ) => {
    const polygon = createPolygonFromBox(boxLngLat);
    addPolygonToMap(polygon, layers, id, colour);
    return polygon;
  };

  export const getVisibleVesselListIds = (visibleFleetIds: FleetVisibility[]) =>
    visibleFleetIds
      .filter((fleet) => fleet.visible)
      .map((fleet) => fleet.list.vessel_list_id);

  export const filterFleetsByVesselType = (
    fleetColouredByVesselType: FleetColouredByVesselType[],
    visibleVesselListIds: string[]
  ) =>
    fleetColouredByVesselType.filter((fleet) =>
      visibleVesselListIds.includes(fleet.vessel_list_id)
    );

  export const filterFleetsBySelectedColour = (
    fleetColouredBySelectedColour: FleetColouredBySelectedColour[],
    visibleVesselListIds: string[]
  ) =>
    fleetColouredBySelectedColour.filter((fleet) =>
      visibleVesselListIds.includes(fleet.vessel_list_id)
    );

  export const getFleetsToDisplayBySelectedColour = (
    fleetColouredBySelectedColour: FleetColouredBySelectedColour[],
    displayByVesselTypeIds: string[]
  ) =>
    fleetColouredBySelectedColour.filter(
      (fleet) => !displayByVesselTypeIds.includes(fleet.vessel_list_id)
    );

  export const applyColoursToMap = (
    mapInitialised: boolean,
    visibleVesselListIds: string[],
    filteredFleetColouredByVesselType: FleetColouredByVesselType[],
    fleetsToDisplayBySelectedColour: FleetColouredBySelectedColour[],
    filteredFleetColouredBySelectedColour: FleetColouredBySelectedColour[],
    defaultVesselColour: string
  ) => {
    if (mapInitialised && visibleVesselListIds.length > 0) {
      if (
        filteredFleetColouredByVesselType.length > 0 &&
        fleetsToDisplayBySelectedColour.length > 0
      ) {
        MapHelpers.setFleetIconsToColourOptions(
          MapLayer.MY_FLEET_VESSELS,
          filteredFleetColouredByVesselType,
          fleetsToDisplayBySelectedColour,
          defaultVesselColour
        );
      } else if (
        filteredFleetColouredByVesselType.length > 0 &&
        fleetsToDisplayBySelectedColour.length === 0
      ) {
        if (filteredFleetColouredByVesselType.length > 0) {
          MapHelpers.setFleetVesselTypeMultiColour(
            MapLayer.MY_FLEET_VESSELS,
            filteredFleetColouredByVesselType,
            defaultVesselColour
          );
        }
      } else {
        if (filteredFleetColouredByVesselType.length > 0) {
          MapHelpers.setFleetVesselTypeMultiColour(
            MapLayer.MY_FLEET_VESSELS,
            filteredFleetColouredByVesselType,
            defaultVesselColour
          );
        }

        if (filteredFleetColouredBySelectedColour.length > 0) {
          MapHelpers.setFleetIconsToColour(
            MapLayer.MY_FLEET_VESSELS,
            filteredFleetColouredBySelectedColour,
            defaultVesselColour
          );
        }
      }

      if (
        filteredFleetColouredByVesselType.length === 0 &&
        filteredFleetColouredBySelectedColour.length === 0
      ) {
        MapHelpers.setVesselSingleColour(
          MapLayer.MY_FLEET_VESSELS,
          defaultVesselColour
        );
      }
    }
  };

  export const getFilteredFleetVessels = (
    vesselFilter: FleetsVessel[],
    fleetsVessels: NormalisedFleetsVessels | null
  ) => {
    if (vesselFilter.length === 0 && fleetsVessels) {
      return fleetsVessels;
    }

    return {
      byId: Object.fromEntries(
        vesselFilter.map((vessel) => [vessel.vessel_id, vessel])
      ),
      idsByImoMmsi: Object.fromEntries(
        vesselFilter.map((vessel) => [
          `${vessel.imo}-${vessel.mmsi}`,
          [vessel.vessel_id],
        ])
      ),
      allIds: vesselFilter.map((vessel) => vessel.vessel_id),
    };
  };

  export const updateLayerPaintProperty = (
    layerId: string,
    property: string,
    value: any
  ) => {
    const map = getMapInstance();
    if (map && map.getLayer(layerId)) {
      map.setPaintProperty(layerId, property as any, value);
    }
  };
}

export default MapHelpers;
